import { apiInstance } from "@/utils/axios";
import { LOCALSTORAGE } from "@/utils/constants";
import {
  setupAxiosInterceptors,
  setupRefreshTimer,
} from "@/utils/tokenRefresh";
import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";
import { computed, inject, ref } from "vue";

function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = window.atob(base64);
    const payload = JSON.parse(jsonPayload);

    if (
      !payload.sub ||
      !payload.iat ||
      !payload.exp ||
      !payload.nbf ||
      !payload.jti
    ) {
      console.warn("JWT token missing required fields");
    }

    return payload;
  } catch (error) {
    console.error("Error parsing JWT:", error);
    return null;
  }
}

function validatePassword(password) {
  const minLength = 8;
  const maxLength = 64;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[@$!%*?&]/.test(password);

  if (password.length < minLength)
    throw new Error(`Password must be at least ${minLength} characters`);
  if (password.length > maxLength)
    throw new Error(`Password must not exceed ${maxLength} characters`);
  if (!hasUpperCase)
    throw new Error("Password must contain at least one uppercase letter");
  if (!hasLowerCase)
    throw new Error("Password must contain at least one lowercase letter");
  if (!hasNumbers) throw new Error("Password must contain at least one number");
  if (!hasSpecialChar)
    throw new Error(
      "Password must contain at least one special character (@$!%*?&)"
    );
}

export const useContractorAuthStore = defineStore("contractorAuthStore", () => {
  // State
  const accessToken = ref(
    localStorage.getItem(LOCALSTORAGE.CONTRACTOR_ACCESS_TOKEN) || null
  );
  const userId = ref(
    localStorage.getItem(LOCALSTORAGE.CONTRACTOR_USER_ID) || null
  );
  const companyId = ref(
    localStorage.getItem(LOCALSTORAGE.CONTRACTOR_COMPANY_ID) || null
  );
  const defaultAccess = ref(
    JSON.parse(
      localStorage.getItem(LOCALSTORAGE.CONTRACTOR_DEFAULT_ACCESS) || "{}"
    )
  );
  const availableIds = ref({
    company_ids: JSON.parse(
      localStorage.getItem(LOCALSTORAGE.COMPANY_IDS) || "[]"
    ),
    project_ids: JSON.parse(
      localStorage.getItem(LOCALSTORAGE.PROJECT_IDS) || "[]"
    ),
    unit_ids: JSON.parse(localStorage.getItem(LOCALSTORAGE.UNIT_IDS) || "[]"),
  });
  const currentUnit = ref(
    JSON.parse(localStorage.getItem(LOCALSTORAGE.CURRENT_UNIT) || "null")
  );
  const currentProject = ref(
    JSON.parse(localStorage.getItem(LOCALSTORAGE.CURRENT_PROJECT) || "null")
  );
  const currentCompany = ref(
    JSON.parse(localStorage.getItem(LOCALSTORAGE.CURRENT_COMPANY) || "null")
  );
  const user = ref(null);
  const isChangePasswordLoading = ref(false);
  const isRefreshingToken = ref(false);
  const refreshTokenTimeout = ref(null);
  const isInitialized = ref(false);

  const signin = ref({
    user: { email: "", password: "", remember_me: false },
    isLoading: false,
    response: null,
  });

  // Getters
  const isAuthenticated = computed(() => {
    if (!accessToken.value) return false;
    const payload = parseJwt(accessToken.value);
    return payload && payload.exp > Math.floor(Date.now() / 1000);
  });
  const getSigninResponse = computed(() => signin.value.response);
  const isSigninLoading = computed(() => signin.value.isLoading);
  const getUserId = computed(() => userId.value);
  const isRefreshingTokenStatus = computed(() => isRefreshingToken.value);
  const getAuthHeaders = computed(() => ({
    Authorization: accessToken.value ? `Bearer ${accessToken.value}` : "",
    "X-Company-ID": companyId.value || "",
    "Content-Type": "application/json",
  }));

  const posthog = inject("posthog", null);

  // Initialize interceptors for contractor specifically
  setupAxiosInterceptors(false, true);

  // Actions
  async function initializeAuth() {
    if (isInitialized.value) return;

    try {
      if (accessToken.value) {
        const tokenData = parseJwt(accessToken.value);
        const now = Math.floor(Date.now() / 1000);

        if (tokenData && tokenData.exp < now) {
          try {
            await refreshToken();
          } catch (error) {
            clearAuthData();
            return;
          }
        }

        apiInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${accessToken.value}`;
        const userData = await getAuthenticatedUser();

        if (userData) {
          user.value = userData;
          if (userData.role) {
            localStorage.setItem(LOCALSTORAGE.CONTRACTOR_ROLE, userData.role);
          }
          if (refreshTokenTimeout.value) {
            clearTimeout(refreshTokenTimeout.value);
          }
          refreshTokenTimeout.value = setupRefreshTimer(
            accessToken.value,
            refreshToken,
            false,
            true
          );
        }
      }
    } catch (error) {
      console.error("Auth initialization failed:", error);
      clearAuthData();
    } finally {
      isInitialized.value = true;
    }
  }

  async function signIn() {
    signin.value.isLoading = true;

    try {
      const response = await apiInstance.post(
        "api/contractor/auth/login",
        {
          email: signin.value.user.email,
          password: signin.value.user.password,
          remember_me: signin.value.user.remember_me,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      signin.value.response = response.data;

      if (
        response.data.status === "error" ||
        response.data.status === "WRONG_CREDENTIALS_ERROR"
      ) {
        throw new Error(response.data.message || "Login failed");
      }

      const {
        access_token,
        user: userData,
        companies,
        current_company,
      } = response.data;

      if (!access_token)
        throw new Error("Login successful but no access token received");
      if (!userData)
        throw new Error("Login successful but no user data received");

      setAccessToken(access_token);
      user.value = userData;
      isInitialized.value = true;

      if (userData?.role) {
        localStorage.setItem(LOCALSTORAGE.CONTRACTOR_ROLE, userData.role);
      }

      if (current_company) {
        setCurrentCompany(current_company);
      } else if (companies?.length > 0) {
        setCurrentCompany(companies[0]);
      }

      if (posthog) {
        posthog.identify(userData.id, {
          email: userData.email,
          name: userData.name,
        });
      }

      Sentry.setUser({ id: userData.id, email: userData.email });

      return response.data;
    } catch (error) {
      console.error("Login error:", error);

      if (
        error.response?.status === 401 ||
        error.message?.includes("access token") ||
        error.message?.includes("credentials")
      ) {
        clearAuthData();
      }

      signin.value.response = {
        status: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Login failed. Please try again.",
      };

      throw error;
    } finally {
      signin.value.isLoading = false;
    }
  }

  async function refreshToken() {
    if (!accessToken.value) {
      throw new Error("No access token available for refresh");
    }

    try {
      isRefreshingToken.value = true;
      const response = await apiInstance.post(
        "api/contractor/auth/refresh",
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken.value}`,
          },
        }
      );

      const { access_token, user: userData } = response.data;
      if (!access_token) {
        throw new Error("No access token received during refresh");
      }

      setAccessToken(access_token);

      if (userData) {
        user.value = userData;
        if (userData.role) {
          localStorage.setItem(LOCALSTORAGE.CONTRACTOR_ROLE, userData.role);
        }
      }

      if (refreshTokenTimeout.value) {
        clearTimeout(refreshTokenTimeout.value);
      }
      refreshTokenTimeout.value = setupRefreshTimer(
        access_token,
        refreshToken,
        false,
        true
      );

      return response.data;
    } catch (error) {
      console.error("Token refresh failed:", error);

      if (error.response) {
        switch (error.response.status) {
          case 401:
            clearAuthData();
            break;
          case 403:
            console.error(
              "Access denied. Please use the appropriate portal for your role."
            );
            break;
          case 500:
            console.error("Server error occurred during token refresh");
            break;
          default:
            console.error(
              "Token refresh failed:",
              error.response.data?.message || error.message
            );
        }
      }

      throw error;
    } finally {
      isRefreshingToken.value = false;
    }
  }

  async function logout(skipApiCall = false) {
    try {
      if (!skipApiCall) {
        await apiInstance.post(
          "api/contractor/auth/logout",
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${accessToken.value}`,
            },
          }
        );
      }
      clearAuthData();
    } catch (error) {
      clearAuthData();
      throw error;
    }
  }

  async function getAuthenticatedUser() {
    try {
      const response = await apiInstance.get("api/contractor/auth/me", {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${accessToken.value}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async function changePassword(payload) {
    try {
      isChangePasswordLoading.value = true;
      const response = await apiInstance.post(
        "api/contractor/auth/change-password",
        {
          current_password: payload.current_password,
          new_password: payload.new_password,
          new_password_confirmation: payload.new_password_confirmation,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken.value}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isChangePasswordLoading.value = false;
    }
  }

  async function forgetPassword(email) {
    try {
      const response = await apiInstance.post(
        "api/contractor/auth/forget-password",
        { email },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async function resetPassword(payload) {
    try {
      validatePassword(payload.password);

      const response = await apiInstance.post(
        "api/contractor/auth/reset-password",
        {
          token: payload.token,
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      clearAuthData();

      return {
        success: true,
        redirectPath: "/contractor/login?reset=success",
        data: response.data,
      };
    } catch (error) {
      let message =
        error.response?.data?.message ||
        error.message ||
        "Password reset failed";
      if (error.message.includes("Password must")) message = error.message;

      return {
        success: false,
        error: message,
      };
    }
  }

  function clearAuthData() {
    if (refreshTokenTimeout.value) {
      clearTimeout(refreshTokenTimeout.value);
      refreshTokenTimeout.value = null;
    }

    accessToken.value = null;
    userId.value = null;
    companyId.value = null;
    defaultAccess.value = {};
    availableIds.value = { company_ids: [], project_ids: [], unit_ids: [] };
    currentUnit.value = null;
    currentProject.value = null;
    currentCompany.value = null;
    user.value = null;

    localStorage.removeItem(LOCALSTORAGE.CONTRACTOR_ACCESS_TOKEN);
    localStorage.removeItem(LOCALSTORAGE.CONTRACTOR_USER_ID);
    localStorage.removeItem(LOCALSTORAGE.CONTRACTOR_ROLE);
    localStorage.removeItem(LOCALSTORAGE.CONTRACTOR_NAME);
    localStorage.removeItem(LOCALSTORAGE.CONTRACTOR_COMPANY_ID);
    localStorage.removeItem(LOCALSTORAGE.CONTRACTOR_DEFAULT_ACCESS);
    localStorage.removeItem(LOCALSTORAGE.DEFAULT_ACCESS);
    localStorage.removeItem(LOCALSTORAGE.COMPANY_IDS);
    localStorage.removeItem(LOCALSTORAGE.PROJECT_IDS);
    localStorage.removeItem(LOCALSTORAGE.UNIT_IDS);
    localStorage.removeItem("current_unit");
    localStorage.removeItem("current_project");
    localStorage.removeItem("current_company");
    localStorage.removeItem("company-id");
    localStorage.removeItem("project_id");

    delete apiInstance.defaults.headers.common["Authorization"];
    delete apiInstance.defaults.headers.common["X-Company-ID"];
  }

  function updateSigninUserData(payload) {
    signin.value.user = { ...signin.value.user, ...payload };
  }

  function setAccessToken(token) {
    if (!token) return;

    const payload = parseJwt(token);
    if (payload && !payload.role?.includes("contractor")) {
      console.error("Invalid token type for contractor store");
      return;
    }

    accessToken.value = token;
    localStorage.setItem(LOCALSTORAGE.CONTRACTOR_ACCESS_TOKEN, token);
    apiInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    if (payload) {
      const { user_id, role, name, company_id, default_access } = payload;

      if (user_id) {
        userId.value = user_id;
        localStorage.setItem(
          LOCALSTORAGE.CONTRACTOR_USER_ID,
          user_id.toString()
        );
      }

      if (role) {
        localStorage.setItem(LOCALSTORAGE.CONTRACTOR_ROLE, role);
      }

      if (name) {
        localStorage.setItem(LOCALSTORAGE.CONTRACTOR_NAME, name);
      }

      if (company_id) {
        companyId.value = company_id;
        localStorage.setItem(
          LOCALSTORAGE.CONTRACTOR_COMPANY_ID,
          company_id.toString()
        );
        apiInstance.defaults.headers.common["X-Company-ID"] =
          company_id.toString();
      }

      if (default_access) {
        defaultAccess.value = default_access;
        localStorage.setItem(
          LOCALSTORAGE.CONTRACTOR_DEFAULT_ACCESS,
          JSON.stringify(default_access)
        );

        if (default_access.company_id && !companyId.value) {
          companyId.value = default_access.company_id;
          localStorage.setItem(
            LOCALSTORAGE.CONTRACTOR_COMPANY_ID,
            default_access.company_id.toString()
          );
          apiInstance.defaults.headers.common["X-Company-ID"] =
            default_access.company_id.toString();
        }

        const storedUnit = localStorage.getItem("current_unit");
        const storedProject = localStorage.getItem("current_project");
        const storedCompany = localStorage.getItem("current_company");

        if (!storedCompany && default_access.company_id)
          setCurrentCompany({ id: default_access.company_id });
        if (!storedProject && default_access.project_id)
          setCurrentProject({ id: default_access.project_id });
        if (!storedUnit && default_access.unit_id)
          setCurrentUnit({ id: default_access.unit_id });
      }

      if (refreshTokenTimeout.value) clearTimeout(refreshTokenTimeout.value);
      refreshTokenTimeout.value = setupRefreshTimer(
        token,
        refreshToken,
        false,
        true
      );
    }
  }

  function setCurrentUnit(unit) {
    if (!unit?.id) return;
    const unitId = typeof unit === "object" ? unit.id : parseInt(unit);
    currentUnit.value = unitId;
    localStorage.setItem("current_unit", String(unitId));
  }

  function setCurrentProject(project) {
    if (!project?.id) return;
    const projectId =
      typeof project === "object" ? project.id : parseInt(project);
    currentProject.value = projectId;
    localStorage.setItem("current_project", String(projectId));
    localStorage.setItem("project_id", String(projectId));
  }

  function setCurrentCompany(company) {
    if (!company?.company_id) {
      console.error("Invalid company object:", company);
      return;
    }

    const companyIdValue = company.company_id;
    currentCompany.value = companyIdValue;
    companyId.value = companyIdValue;
    localStorage.setItem("current_company", String(companyIdValue));
    localStorage.setItem("company-id", String(companyIdValue));
    apiInstance.defaults.headers.common["X-Company-ID"] =
      String(companyIdValue);
  }

  return {
    // State
    accessToken,
    userId,
    companyId,
    signin,
    user,
    isChangePasswordLoading,
    isRefreshingToken,
    isInitialized,
    defaultAccess,
    availableIds,
    currentUnit,
    currentProject,
    currentCompany,

    // Getters
    isAuthenticated,
    getSigninResponse,
    isSigninLoading,
    getUserId,
    isRefreshingTokenStatus,
    getAuthHeaders,

    // Actions
    signIn,
    refreshToken,
    logout,
    clearAuthData,
    updateSigninUserData,
    setAccessToken,
    initializeAuth,
    getAuthenticatedUser,
    changePassword,
    forgetPassword,
    resetPassword,
    setCurrentUnit,
    setCurrentProject,
    setCurrentCompany,
  };
});
